import { useCallback, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useMovementPageData } from 'src/app/pages/movement/pageContext';

import type { PetitionsInMovementResponse } from './api/getPetitionsInMovement';
import { getPetitionsInMovement } from './api/getPetitionsInMovement';

type Result = ModelHookResult<
	{
		petitions: PetitionsInMovementResponse['items'];
		hasNextPage: boolean;
		isLoading: boolean;
		isFetchingNextPage: boolean;
		isError: boolean;
	},
	{
		loadMore: () => Promise<void>;
		trackMovementPetitionClick: (petitionId: string) => void;
	}
>;

const PETITIONS_PAGE_SIZE = 8;

export function usePageBody(): Result {
	const utilityContext = useUtilityContext();
	const {
		data: {
			movement: { id: movementId },
		},
	} = useMovementPageData();
	const track = useTracking();

	const { isLoading, isFetchingNextPage, data, isError, fetchNextPage, hasNextPage } = useInfiniteQuery({
		queryKey: ['petitions-in-movement', movementId],
		queryFn: async ({ pageParam }) => {
			const offset = pageParam * PETITIONS_PAGE_SIZE;
			const limit = PETITIONS_PAGE_SIZE;
			const response = await getPetitionsInMovement({ movementId, limit, offset }, utilityContext);
			return { ...response, currentPage: pageParam };
		},
		initialPageParam: 0,
		getNextPageParam: (previousResult) => {
			return previousResult.lastPage ? undefined : previousResult.currentPage + 1;
		},
		refetchOnMount: true,
	});

	const loadMore = useCallback(async () => {
		if (!isLoading) {
			await fetchNextPage();
		}
	}, [isLoading, fetchNextPage]);

	const petitions = useMemo(() => (data ? data.pages.flatMap((page) => page.items) : []), [data]);

	return {
		data: {
			petitions,
			hasNextPage,
			isLoading,
			isFetchingNextPage,
			isError,
		},
		actions: {
			loadMore,
			trackMovementPetitionClick: useCallback(
				(petitionId: string) => {
					void track('movement_petition_click', {
						movement_id: movementId,
						petition_id: petitionId,
						view_context: 'movement_show',
					});
				},
				[movementId, track],
			),
		},
	};
}
